import { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { TaskKanbanCard } from "./TaskKanbanCard";
import type { StatusPalette } from "./statusPalette";
import type { TaskRead } from "@/api/types";

/** What happens when a task is dropped into a column. Dynamic statuses
 *  PATCH `task.status`; the legacy fallback flips is_completed via the
 *  complete / reopen endpoints. */
export type KanbanColumnApply =
  | { kind: "status"; statusKey: string; isDone: boolean }
  | { kind: "boolean"; isCompleted: boolean };

export interface KanbanColumnConfig extends StatusPalette {
  /** Stable id used by dnd-kit for drop detection. */
  id: string;
  /** Display label. */
  label: string;
  /** How to mutate a dropped task to land in this column. */
  apply: KanbanColumnApply;
}

interface TaskKanbanColumnProps {
  column: KanbanColumnConfig;
  tasks: TaskRead[];
  projectSlug: string;
  canEdit: boolean;
  canDelete: boolean;
  /** Show the create-task affordance at the bottom of the column. */
  showAddButton: boolean;
  /** Collapsed columns render header + count only — body and cards are
   *  hidden. The drop zone stays active so a card dragged onto a
   *  collapsed Done column still marks it done. */
  collapsed?: boolean;
  onToggleCollapsed?: (column: KanbanColumnConfig) => void;
  /** Render every card with the de-emphasised "done" treatment
   *  (opacity 70 + line-through title). Set on done columns so cards
   *  stay visible after a task is completed but read clearly as
   *  archived. */
  dimCards?: boolean;
  /** Optional soft cap on the number of cards displayed in this
   *  column. When tasks.length > cap, the first `cap` are rendered
   *  followed by a "Show all (N)" button that toggles a local
   *  show-all state. Used on done columns so a long list of completed
   *  work doesn't dominate the viewport. */
  cap?: number;
  onAddTask: (column: KanbanColumnConfig) => void;
  onOpenDetail: (task: TaskRead) => void;
  onAskDelete?: (task: TaskRead) => void;
  loading?: boolean;
}

export function TaskKanbanColumn({
  column,
  tasks,
  projectSlug,
  canEdit,
  canDelete,
  showAddButton,
  collapsed = false,
  onToggleCollapsed,
  dimCards = false,
  cap,
  onAddTask,
  onOpenDetail,
  onAskDelete,
  loading = false,
}: TaskKanbanColumnProps) {
  const { isOver, setNodeRef } = useDroppable({ id: column.id });
  const collapsible = !!onToggleCollapsed;

  // Local "show all" override for the cap. Resets implicitly when the
  // column unmounts (e.g. when the page status filter hides this
  // column); that's fine — the user re-clicks if they need it again.
  const [showAll, setShowAll] = useState(false);
  const hasCap = cap !== undefined && tasks.length > cap;
  const visibleTasks = !hasCap || showAll ? tasks : tasks.slice(0, cap);
  const hiddenCount = hasCap && !showAll ? tasks.length - cap : 0;

  return (
    <div className="flex min-w-0 flex-1 flex-col">
      {/* Header — colored border-top, dot + label + count. The whole
       *  header is the toggle target when collapsible, so users can
       *  expand/collapse with a single click anywhere along it. */}
      <div className={cn("border-t-[3px]", column.borderClass)}>
        <div className="flex items-center justify-between gap-2 px-2 pt-3 pb-2">
          <button
            type="button"
            onClick={() =>
              collapsible && onToggleCollapsed?.(column)
            }
            disabled={!collapsible}
            className={cn(
              "flex min-w-0 flex-1 items-center gap-2 rounded-md text-left",
              collapsible &&
                "cursor-pointer transition-colors hover:bg-slate-50",
            )}
          >
            {collapsible &&
              (collapsed ? (
                <ChevronRight
                  className="h-3.5 w-3.5 shrink-0 text-slate-400"
                  aria-hidden
                />
              ) : (
                <ChevronDown
                  className="h-3.5 w-3.5 shrink-0 text-slate-400"
                  aria-hidden
                />
              ))}
            <span
              className={cn("h-2 w-2 rounded-full", column.dotClass)}
              aria-hidden
            />
            <span className="truncate text-sm font-semibold text-slate-700">
              {column.label}
            </span>
            <span
              className={cn(
                "rounded-full px-1.5 py-0 text-[11px] font-medium tabular-nums",
                column.softBgClass,
                column.softTextClass,
              )}
            >
              {tasks.length}
            </span>
          </button>
        </div>
      </div>

      {/* Body — droppable area. Stays mounted (and droppable) when the
       *  column is collapsed so drag-and-drop into Done still works
       *  without expanding it first. */}
      <div
        ref={setNodeRef}
        className={cn(
          "flex flex-col gap-2 rounded-b-lg border border-t-0 border-transparent transition-colors",
          collapsed
            ? "p-0"
            : "flex-1 p-2 max-h-[calc(100vh-300px)] overflow-y-auto",
          // dnd-kit isOver gives us the dashed-indigo highlight from the
          // spec.
          isOver && "border-indigo-300/40 border-dashed",
          isOver && column.hoverBgClass,
        )}
      >
        {!collapsed && (
          <>
            <SortableContext
              items={visibleTasks.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {visibleTasks.map((t) => (
                <TaskKanbanCard
                  key={t.id}
                  task={t}
                  projectSlug={projectSlug}
                  canDelete={canDelete}
                  dimmed={dimCards}
                  onOpenDetail={onOpenDetail}
                  onDelete={onAskDelete}
                />
              ))}
            </SortableContext>

            {hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="mt-1 inline-flex h-8 items-center justify-center rounded-lg text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
              >
                Show all ({tasks.length})
              </button>
            )}

            {loading && tasks.length === 0 && (
              <>
                <div className="h-20 animate-pulse rounded-xl bg-white shadow-card-rest" />
                <div className="h-20 animate-pulse rounded-xl bg-white shadow-card-rest" />
              </>
            )}

            {!loading && tasks.length === 0 && (
              <div className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-[13px] text-slate-400">
                No tasks
              </div>
            )}

            {showAddButton && canEdit && (
              <button
                type="button"
                onClick={() => onAddTask(column)}
                className={cn(
                  "mt-1 inline-flex h-8 items-center justify-center gap-1.5 rounded-lg text-xs font-medium text-slate-500 transition-colors",
                  column.hoverSoftBgClass,
                  column.hoverTextClass,
                )}
              >
                <Plus className="h-3.5 w-3.5" />
                New task
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
